import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, CalendarClock, Cake, DoorClosed, KeyRound, Newspaper, Radio, ShieldCheck, UtensilsCrossed } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SignalFolder } from "../../interfaces/signalFolder";

/** One icon per folder, from the app's icon set (the folders' emojis are not used here). */
const FOLDER_ICON: Record<string, LucideIcon> = {
    "restaurant-openings": UtensilsCrossed,
    "future-openings": CalendarClock,
    "restaurant-owner-change": KeyRound,
    "new-establishment": Building2,
    anniversaries: Cake,
    "restaurant-closing": DoorClosed,
    "hygiene-control": ShieldCheck,
    "press-signals": Newspaper,
};

/**
 * A signal folder on /signals: icon, title, two lines of what it finds, and where it leads.
 * Kept compact so every folder fits on one screen. A standalone folder opens its own page.
 */
export default function SignalFolderCard({ folder }: { folder: SignalFolder }) {
    const navigate = useNavigate();
    const Icon = FOLDER_ICON[folder.id] || Radio;
    const to = folder.id === "press-signals" ? "/actualites" : `/signals/folder/${folder.id}`;

    return (
        <button
            type="button"
            onClick={() => navigate(to)}
            className="group flex h-full flex-col rounded-2xl border border-border bg-surface p-5 text-left font-sans transition-colors duration-150 hover:border-border-strong hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-surface-sunken text-ink">
                <Icon className="h-[18px] w-[18px]" />
            </span>
            <span className="mt-3 text-base font-semibold text-ink">{folder.title}</span>
            <span className="mt-1 line-clamp-2 text-sm leading-relaxed text-ink-muted" title={folder.description}>
                {folder.tagline || folder.description}
            </span>
            <span className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-medium text-ink">
                {folder.id === "press-signals" ? "Voir les actualités" : "Voir les signaux"}
                <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" />
            </span>
        </button>
    );
}
